const LAYER_ID = 'admicity-walkthrough-layer';
const BRAND = '#0A8572';
const Z_INDEX = 2147483000;
// AD-2653: Felicity's outline is amber, so it shows on the teal menu bar as well as on a white page.
const BRIGHT = '#FFB000';
const RING = {
    border: `3px solid ${BRAND}`,
    boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.95), 0 0 0 5px rgba(10, 133, 114, 0.38), 0 6px 20px rgba(0, 0, 0, 0.3)',
    animation: ''
};
const BRIGHT_RING = {
    border: `4px solid ${BRIGHT}`,
    boxShadow: '0 0 0 2px rgba(28, 30, 38, 0.85), 0 0 18px 6px rgba(255, 176, 0, 0.7)',
    animation: 'admicity-walkthrough-pulse 1.4s ease-in-out infinite'
};
const PULSE = '@keyframes admicity-walkthrough-pulse {' +
    ' 0%, 100% { box-shadow: 0 0 0 2px rgba(28, 30, 38, 0.85), 0 0 18px 6px rgba(255, 176, 0, 0.7); }' +
    ' 50% { box-shadow: 0 0 0 2px rgba(28, 30, 38, 0.85), 0 0 28px 12px rgba(255, 176, 0, 0.45); } }' +
    ' @media (prefers-reduced-motion: reduce) { #admicity-walkthrough-layer div { animation: none !important; } }';

const state = {
    layer: null,
    ring: null,
    caption: null,
    cursor: null,
    ripple: null,
    element: null,
    others: [],
    frame: null,
    pointerX: null,
    pointerY: null,
    trackingPointer: false
};

const build = () => {
    if (state.layer) {
        return state.layer;
    }

    const layer = document.createElement('div');
    layer.id = LAYER_ID;
    layer.setAttribute('aria-hidden', 'true');
    Object.assign(layer.style, {
        position: 'fixed',
        inset: '0',
        pointerEvents: 'none',
        zIndex: String(Z_INDEX),
        display: 'none'
    });

    const ring = document.createElement('div');
    Object.assign(ring.style, {
        position: 'fixed',
        boxSizing: 'border-box',
        ...RING,
        borderRadius: '8px',
        transition: 'opacity 140ms ease',
        opacity: '0',
        left: '0',
        top: '0',
        width: '0',
        height: '0'
    });

    const caption = document.createElement('div');
    Object.assign(caption.style, {
        position: 'fixed',
        maxWidth: '380px',
        padding: '6px 12px',
        borderRadius: '6px',
        backgroundColor: BRAND,
        color: '#ffffff',
        font: '500 14px/1.35 Roboto, Helvetica, Arial, sans-serif',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
        opacity: '0',
        left: '0',
        top: '0'
    });

    const cursor = document.createElement('div');
    Object.assign(cursor.style, {
        position: 'fixed',
        width: '24px',
        height: '24px',
        marginLeft: '-1px',
        marginTop: '-1px',
        opacity: '0',
        left: '0',
        top: '0',
        transformOrigin: '2px 2px',
        transition: 'transform 90ms ease'
    });
    cursor.innerHTML =
        '<svg viewBox="0 0 24 24" width="24" height="24">' +
        '<path d="M1 1 L1 17 L5.2 13.1 L8 19.5 L10.6 18.3 L7.9 12.1 L13.6 11.8 Z"' +
        ' fill="#1c1e26" stroke="#ffffff" stroke-width="1.4" stroke-linejoin="round"/>' +
        '</svg>';

    const ripple = document.createElement('div');
    Object.assign(ripple.style, {
        position: 'fixed',
        width: '0',
        height: '0',
        marginLeft: '0',
        marginTop: '0',
        borderRadius: '50%',
        border: `2px solid ${BRAND}`,
        backgroundColor: 'rgba(10, 133, 114, 0.18)',
        opacity: '0',
        left: '0',
        top: '0'
    });

    const pulse = document.createElement('style');
    pulse.textContent = PULSE;
    layer.append(ring, caption, cursor, ripple, pulse);
    document.body.appendChild(layer);

    state.layer = layer;
    state.ring = ring;
    state.caption = caption;
    state.cursor = cursor;
    state.ripple = ripple;

    return layer;
};

const place = () => {
    state.frame = null;

    if (!state.element || !state.element.isConnected) {
        return;
    }

    // AD-2653: the same button on several rows, such as Edit contact, is outlined together.
    const rects = [state.element, ...state.others.filter(other => other.isConnected)].map(element => element.getBoundingClientRect());
    const rect = {
        top: Math.min(...rects.map(r => r.top)),
        left: Math.min(...rects.map(r => r.left)),
        width: Math.max(...rects.map(r => r.right)) - Math.min(...rects.map(r => r.left)),
        height: Math.max(...rects.map(r => r.bottom)) - Math.min(...rects.map(r => r.top))
    };
    const padding = 6;
    const top = rect.top - padding;
    const left = rect.left - padding;
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    Object.assign(state.ring.style, {
        left: `${left}px`,
        top: `${top}px`,
        width: `${width}px`,
        height: `${height}px`,
        opacity: '1'
    });

    if (state.caption.textContent) {
        const captionHeight = state.caption.offsetHeight || 30;
        const above = top - captionHeight - 8;
        Object.assign(state.caption.style, {
            left: `${Math.max(8, Math.min(left, window.innerWidth - state.caption.offsetWidth - 8))}px`,
            top: `${above > 8 ? above : top + height + 8}px`,
            opacity: '1'
        });
    }

    state.frame = requestAnimationFrame(place);
};

const startTracking = (element) => {
    state.element = element;
    if (state.frame === null) {
        state.frame = requestAnimationFrame(place);
    }
};

const stopTracking = () => {
    if (state.frame !== null) {
        cancelAnimationFrame(state.frame);
        state.frame = null;
    }
    state.element = null;
};

const settled = (timeout = 700) => new Promise(resolve => {
    let lastTop = null;
    let still = 0;
    const started = Date.now();

    const check = () => {
        if (!state.element || !state.element.isConnected) {
            resolve();
            return;
        }

        const top = Math.round(state.element.getBoundingClientRect().top);
        still = top === lastTop ? still + 1 : 0;
        lastTop = top;

        if (still >= 3 || Date.now() - started > timeout) {
            resolve();
            return;
        }

        requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
});

export const show = async (element, {caption = '', scroll = true, bright = false, others = []} = {}) => {
    build();
    state.others = others;
    Object.assign(state.ring.style, bright ? BRIGHT_RING : RING);
    state.layer.style.display = 'block';
    state.caption.textContent = caption || '';
    state.caption.style.opacity = caption ? '1' : '0';

    startTracking(element);

    if (scroll) {
        element.scrollIntoView({block: 'center', inline: 'nearest', behavior: 'smooth'});
        await settled();
    }

    return element;
};

export const hide = () => {
    stopTracking();
    state.others = [];

    if (!state.layer) {
        return;
    }

    state.ring.style.opacity = '0';
    state.caption.style.opacity = '0';
    state.caption.textContent = '';

    if (!state.trackingPointer) {
        state.layer.style.display = 'none';
    }
};

const drawPointer = (x, y) => {
    build();
    state.layer.style.display = 'block';
    state.pointerX = x;
    state.pointerY = y;
    Object.assign(state.cursor.style, {
        left: `${x}px`,
        top: `${y}px`,
        opacity: '1'
    });
};

const RIPPLE_SIZE = 42;

const pressPointer = (pressed) => {
    if (!state.cursor) {
        return;
    }

    state.cursor.style.transform = pressed ? 'scale(0.82)' : 'scale(1)';

    if (!pressed || state.pointerX === null) {
        return;
    }

    Object.assign(state.ripple.style, {
        transition: 'none',
        left: `${state.pointerX}px`,
        top: `${state.pointerY}px`,
        width: '0px',
        height: '0px',
        marginLeft: '0px',
        marginTop: '0px',
        opacity: '0.9'
    });

    requestAnimationFrame(() => {
        Object.assign(state.ripple.style, {
            transition: 'width 320ms ease-out, height 320ms ease-out, margin 320ms ease-out, opacity 320ms ease-out',
            width: `${RIPPLE_SIZE}px`,
            height: `${RIPPLE_SIZE}px`,
            marginLeft: `${-RIPPLE_SIZE / 2}px`,
            marginTop: `${-RIPPLE_SIZE / 2}px`,
            opacity: '0'
        });
    });
};

export const followPointer = () => {
    if (state.trackingPointer) {
        return;
    }

    state.trackingPointer = true;
    build();

    document.addEventListener('mousemove', event => drawPointer(event.clientX, event.clientY), true);
    document.addEventListener('mousedown', () => pressPointer(true), true);
    document.addEventListener('mouseup', () => pressPointer(false), true);
};

export const stopFollowingPointer = () => {
    state.trackingPointer = false;
    if (state.cursor) {
        state.cursor.style.opacity = '0';
    }
};

export const pointerPosition = () => ({x: state.pointerX, y: state.pointerY});
